// src/frontend_react/src/utils/fileParser.js
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

export const parseSelectedFile = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("No file provided for parsing."));
      return;
    }

    const reader = new FileReader();
    const fileType = file.name.split('.').pop()?.toLowerCase();

    if (!fileType) {
        reject(new Error("Could not determine file type."));
        return;
    }

    reader.onload = (event) => {
      try {
        const fileContent = event.target.result;
        if (fileType === 'csv') {
          Papa.parse(fileContent, {
            worker: true,
            header: true,
            skipEmptyLines: 'greedy', // Skips lines that are empty or only whitespace
            complete: (results) => {
              if (results.errors.length) {
                const criticalError = results.errors.find(e => e.critical);
                if (criticalError) {
                    console.error("Critical PapaParse error:", criticalError);
                    reject(new Error(`Critical error parsing CSV: ${criticalError.message} (Row: ${criticalError.row})`));
                    return;
                }
                console.warn("PapaParse non-critical errors:", results.errors);
                // Decide if non-critical errors should still reject or proceed with data
              }
              // Ensure headers are strings and handle cases where papaparse might return null/undefined fields
              const headers = (results.meta.fields || (results.data.length > 0 ? Object.keys(results.data[0]) : [])).map(h => String(h || '').trim()).filter(h => h !== '');
              
              const sanitizedData = results.data.map(row => {
                const newRow = {};
                headers.forEach(header => {
                    newRow[header] = row[header] !== undefined && row[header] !== null ? row[header] : '';
                });
                // If original row had more keys than extracted headers (e.g. due to inconsistent CSV)
                // this step ensures we only work with data corresponding to actual headers.
                return newRow;
              });

              resolve({ data: sanitizedData, meta: { fields: headers } });
            },
            error: (error) => {
              console.error("PapaParse file reading error:", error);
              reject(new Error(`Error reading CSV file: ${error.message}`));
            }
          });
        } else if (fileType === 'xlsx' || fileType === 'xls') {
          const workbook = XLSX.read(fileContent, { type: 'binary', cellDates: true }); // cellDates: true for better date handling
          const firstSheetName = workbook.SheetNames[0];
          if (!firstSheetName) {
            reject(new Error("XLSX file contains no sheets."));
            return;
          }
          const worksheet = workbook.Sheets[firstSheetName];
          // Use raw: false to get formatted strings (e.g. dates as strings instead of numbers)
          // header: 1 to get array of arrays for robust header and data extraction
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false, defval: '' }); 

          if (jsonData.length === 0) {
            resolve({ data: [], meta: { fields: [] } });
            return;
          }
          
          const headers = jsonData[0].map(header => String(header || '').trim()).filter(h => h !== ''); // Ensure headers are strings and not empty
          const dataRows = jsonData.slice(1).map(rowArray => {
            let rowObject = {};
            headers.forEach((header, index) => {
              // Ensure rowArray has enough elements for the current header
              rowObject[header] = index < rowArray.length && rowArray[index] !== undefined && rowArray[index] !== null ? rowArray[index] : '';
            });
            return rowObject;
          });
          resolve({ data: dataRows, meta: { fields: headers } });
        } else {
          reject(new Error(`Unsupported file type: ${fileType}. Only CSV, XLSX, XLS are supported.`));
        }
      } catch (error) {
        console.error("Error processing file content:", error);
        reject(new Error(`Error processing file: ${error.message || String(error)}`));
      }
    };

    reader.onerror = (error) => {
      console.error("FileReader error:", error);
      reject(new Error(`Error in FileReader: ${error.message}`));
    };

    if (fileType === 'csv') {
      reader.readAsText(file);
    } else if (fileType === 'xlsx' || fileType === 'xls') {
      reader.readAsBinaryString(file);
    } else {
      reject(new Error(`Unsupported file type: ${fileType}. Cannot initiate read.`));
    }
  });
};